import type { ReactNode } from 'react';
import type { ToastOptions, ToastTone } from '@shining-technologies/ui';

/**
 * Implementation of the admin toast facade. App code imports `toast` from
 * '@/lib/toast' (a re-export of this module), nowhere else; only `KitToaster`
 * imports this file directly — so a test that `vi.mock('@/lib/toast')`s the
 * facade leaves the toaster's registration intact.
 *
 * The admin's ONE toast API — import `toast` from '@/lib/toast', nowhere else.
 *
 * It is an imperative, sonner-shaped facade (`toast.success('Saved')`,
 * `toast.error('Failed', { description })`) over the UI kit's toast stack
 * (`ToastProvider` / `useToast` / `Toaster` from @shining-technologies/ui).
 *
 * Why a facade instead of calling the kit's `useToast()` directly: the kit's
 * API is a hook, but the admin raises toasts from mutation callbacks, query
 * error handlers and plain functions that are not components. `KitToaster`
 * (src/components/feedback/KitToaster.tsx, mounted once in app/providers.tsx)
 * registers the live `useToast()` value here; calls made before it mounts are
 * queued and flushed on registration, so nothing is dropped during boot.
 *
 * Differences from sonner (see admin/UI_KIT_MIGRATION.md → Toasts):
 *   - no `toast.promise` / `toast.loading` / `toast.custom` — raise a toast
 *     with `duration: 0` and replace it by `id` when the work settles;
 *   - `action.onClick` takes no event argument;
 *   - ids are always strings.
 */

export interface ToastCallOptions {
  /** Second line under the title. */
  description?: ReactNode;
  /** Reuse an id to replace that toast in place (and restart its timer). */
  id?: string;
  /** Lifetime in ms; `0` keeps it up until dismissed. Default: 5000. */
  duration?: number;
  /** One button in the toast; clicking it runs `onClick`, then dismisses. */
  action?: { label: string; onClick: () => void };
}

/** What `KitToaster` registers: the kit's `useToast()` value (a subset). */
export interface ToastSink {
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

/** Max toasts on screen (the kit's `ToastProvider limit`) and max queued pre-mount. */
export const TOAST_LIMIT = 4;

let sink: ToastSink | null = null;
let pending: ToastOptions[] = [];
let counter = 0;

/** Called by `KitToaster` on mount. Returns the unregister function. */
export function registerToastSink(next: ToastSink): () => void {
  sink = next;
  const queued = pending;
  pending = [];
  for (const options of queued) next.toast(options);
  return () => {
    if (sink === next) sink = null;
  };
}

function show(tone: ToastTone, title: ReactNode, options: ToastCallOptions = {}): string {
  counter += 1;
  const id = options.id ?? `admin-toast-${counter}`;
  const payload: ToastOptions = { ...options, id, title, tone };
  if (sink) sink.toast(payload);
  else {
    // Same id replaces in place, as the kit does; otherwise keep only the
    // newest TOAST_LIMIT — the stack could not show more than that anyway, and
    // an unbounded queue would leak if the toaster never mounts (e.g. tests).
    pending = [...pending.filter((p) => p.id !== id), payload].slice(-TOAST_LIMIT);
  }
  return id;
}

type ToastFn = (title: ReactNode, options?: ToastCallOptions) => string;

export const toast: ToastFn & {
  success: ToastFn;
  error: ToastFn;
  warning: ToastFn;
  info: ToastFn;
  /** Neutral tone (sonner's `toast.message`). */
  message: ToastFn;
  /** Dismiss one toast by id, or every toast when called with no id. */
  dismiss: (id?: string) => void;
} = Object.assign((title: ReactNode, options?: ToastCallOptions) => show('neutral', title, options), {
  success: (title: ReactNode, options?: ToastCallOptions) => show('success', title, options),
  error: (title: ReactNode, options?: ToastCallOptions) => show('destructive', title, options),
  warning: (title: ReactNode, options?: ToastCallOptions) => show('warning', title, options),
  info: (title: ReactNode, options?: ToastCallOptions) => show('info', title, options),
  message: (title: ReactNode, options?: ToastCallOptions) => show('neutral', title, options),
  dismiss: (id?: string) => {
    if (id === undefined) {
      pending = [];
      sink?.dismissAll();
      return;
    }
    pending = pending.filter((p) => p.id !== id);
    sink?.dismiss(id);
  },
});
